import moment from "moment";
import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { Button, Dropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Spinner from "@components/Spinner/Spinner";
import ServerError from "@components/Error/ServerError";
import PageNotFound from "@components/Error/PageNotFound";
import { CloseIcon, MoreIcon } from "@components/icons";
import ExportSensor from "./ExportSensor";
import { getSensor, sensorPlot, installationPlot, getLastSend } from "../services/sensorCrud";
import MobileBreadcrumb from "@components/Breadcrumb/MobileBreadcrumb";
import SensorChart from "./SensorChart";
import SensorForm from "./Form/SensorForm";
import ReactTable from "@components/ReactTable";
import { createScheduledQueryFn, REQUEST_PRIORITIES } from "@helper/requestPriority";

const style = {
  height: "260px",
  width: "100%",
};
const sensorState = {
  0: "working",
  1: "battery_empty",
  2: "maintenace_needed",
  3: "not_sending",
  4: "unknown",
};

export function SensorPlot({ sensor, plotType, setPlotType, installation }) {
  const [plot, setPlot] = useState();
  const [plotTitle, setPlotTitle] = useState("");
  const { t } = useTranslation();
  const scheduledPlotQuery = createScheduledQueryFn(
    () => (installation ? installationPlot(installation) : sensorPlot(sensor)),
    REQUEST_PRIORITIES.NORMAL,
    "sensorPlot"
  );
  const { isLoading: isPlotLoading } = useQuery(["sensorplot", { sensor }], scheduledPlotQuery, {
    enabled: !!sensor,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const { x, y } = data;
      let sensorType = "resistance";
      let plotTitle = "Resistance in kΩ";
      sensor.soil_moisture_sensor
        ? ((sensorType = "soilMoisture"), (plotTitle = "Soil Moisture in %"))
        : sensor.soil_water_tension_sensor
        ? ((sensorType = "soilWaterTension"), (plotTitle = "Soil Water Tension in kPa"))
        : sensor.dendrometer_sensor
        ? ((sensorType = "dendrometer"), (plotTitle = "Diameter in µm"))
        : (sensorType = "resistance");
      setPlotType(sensorType);
      setPlotTitle(plotTitle);

      let d;
      if (sensorType == "resistance") {
        // d = x[sensor.hardware_serial]?.resistance?.map((value, index) => {
        //   if (y[sensor.hardware_serial].resistance[index]) {
        //     return [value, y[sensor.hardware_serial].resistance[index]];
        //   }
        // });
        const mainData =
          x[sensor.id]?.resistance?.map((value, index) => [
            value,
            y[sensor.id].resistance[index],
          ]) || [];
        // const resis70mcData =
        //   x[sensor.hardware_serial]?.resistance?.map((value, index) => [
        //     value,
        //     y[sensor.hardware_serial].resis_70mc[index],
        //   ]) || [];
        // const resis50mcData =
        //   x[sensor.hardware_serial]?.resistance?.map((value, index) => [
        //     value,
        //     y[sensor.hardware_serial].resis_50mc[index],
        //   ]) || [];

        d = [
          { data: mainData, name: "resistance", type: "line", showSymbol: false }, // Assuming blue for the main resistance line
          // {
          //   data: resis70mcData,
          //   color: GREEN,
          //   name: "resistance for 70%",
          //   type: "line",
          //   showSymbol: false,
          // },
          // {
          //   data: resis50mcData,
          //   color: RED,
          //   name: "resistance for 50%",
          //   type: "line",
          //   showSymbol: false,
          // },
        ];
      }
      if (sensorType == "soilMoisture") {
        const mainData =
          x[sensor.id]?.water_soil?.map((value, index) => [
            value,
            y[sensor.id].water_soil[index],
          ]) || [];
        d = [
          {
            data: mainData,
            color: "blue",
            name: "soil moisture",
            type: "line",
            showSymbol: false,
          }, // Assuming blue for the main resistance line
        ];
      }
      if (sensorType == "soilWaterTension") {
        const mainData =
          x[sensor.id]?.soil_water_tension?.map((value, index) => [
            value,
            y[sensor.id].soil_water_tension[index],
          ]) || [];
        d = [
          {
            data: mainData,
            color: "blue",
            name: "soil water tension",
            type: "line",
            showSymbol: false,
          }, // Assuming blue for the main resistance line
        ];
      }
      if (sensorType == "dendrometer") {
        const mainData =
          x[sensor.id]?.dendrometer?.map((value, index) => [
            value,
            y[sensor.id].dendrometer[index],
          ]) || [];
        d = [
          {
            data: mainData,
            color: "blue",
            name: "diameter",
            type: "line",
            showSymbol: false,
          }, // Assuming blue for the main resistance line
        ];
      }
      const plotNew = d ? [...d] : null;
      setPlot(plotNew);
    },
  });
  return (
    <>
      {isPlotLoading ? (
        <Spin />
      ) : plot ? (
        <SensorChart plot={plot} style={style} type={plotType} plotTitle={plotTitle} />
      ) : (
        <div className=" bg-light text-center" style={{ height: "15vh" }}>
          <img name="empty_project" alt="" width="80" src="/empty_project.svg" />
          <h6 className="my-4">{t("sensor_details.no_data_found")}</h6>
        </div>
      )}
    </>
  );
}

export default function SensorDetail() {
  const [isUpdate, setIsUpdate] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [plotType, setPlotType] = useState("resistance");
  const { mobile } = useSelector((state) => state.mobile);
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const id = query.get("selectedSensor");
  const [lastSend, setLastSend] = useState("");
  const [data, setData] = useState(null);
  // const [lastSendValue, setLastSendValue] = useState("");
  const [value, setValue] = useState("");
  const [sensorType, setSensorType] = useState("");
  const { t } = useTranslation();

  const closeSensorDetail = () => {
    const params = new URLSearchParams(location.search);
    params.delete("selectedSensor");
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`);
  };

  //get sensor detail
  const scheduledGetSensor = createScheduledQueryFn(
    () => getSensor(id),
    REQUEST_PRIORITIES.HIGH,
    "getSensor"
  );
  const {
    data: sensor,
    isLoading,
    isError,
    error,
  } = useQuery(["sensors", { id }], scheduledGetSensor, {
    refetchOnWindowFocus: false,
    retry: 3, // number of times to retry the request before giving up
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // delay between retries in milliseconds
    // retry: false,
    enabled: !!id,
  });

  const scheduledGetLastSend = createScheduledQueryFn(
    () => getLastSend(id),
    REQUEST_PRIORITIES.NORMAL,
    "getLastSend"
  );
  const { isLoading: isLoadingLastSend } = useQuery(["last-sent", { id }], scheduledGetLastSend, {
    refetchInterval: 60000, // every minute
    refetchOnWindowFocus: false,
    retry: 1, // number of times to retry the request before giving up
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // delay between retries in milliseconds
    // retry: false,
    enabled: !!id,
    onSuccess: (data) => {
      setData(data.data);
      setSensorType(data.sensor);
      setValue(data.value);
    },
  });

  const handleEdit = () => {
    setIsUpdate(true);
    setOpenForm(true);
  };

  if (isLoading) return <Spinner height={"80vh"} />;
  if (isError && error.response.status === 404) {
    return <PageNotFound />;
  }
  if (isError) {
    return <ServerError />;
  }

  const now = new Date();
  const hours_passed = (now - new Date(lastSend)) / (1000 * 60 * 60);
  let isSending = false;
  if (hours_passed < 3) {
    isSending = true;
  }

  // Parse localStorage as JSON object
  let isSendingObj;
  try {
    isSendingObj = JSON.parse(localStorage.getItem("isSending"));
  } catch (e) {
    isSendingObj = null;
  }

  if (!isSendingObj || typeof isSendingObj !== "object") {
    isSendingObj = {};
  }

  isSendingObj[sensor.id] = isSending;

  // Store as JSON string
  localStorage.setItem("isSending", JSON.stringify(isSendingObj));
  localStorage.setItem("lastSend", isSending);

  return (
    <>
      {mobile && (
        <>
          <MobileBreadcrumb
            editButton={
              <Button data-testid="sensor-detail-edit" onClick={() => handleEdit()}>
                {t("edit")}
              </Button>
            }
          />
        </>
      )}
      <div>
        <div
          className="top-0 position-sticky d-flex align-items-center gap-2"
          style={{ zIndex: "99", background: "white", padding: "0 12px", height: "55px" }}
        >
          <button
            onClick={closeSensorDetail}
            className="sensor-detail-icon-link border-0 bg-transparent p-1"
            aria-label={t("close")}
            title={t("close")}
          >
            <CloseIcon />
          </button>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <h4 className="mb-0 text-truncate" data-testid="test-sensor-name">
              {sensor.name}
            </h4>
          </div>
          <div className="sensor-detail-panel-actions d-flex align-items-center gap-2">
            <Dropdown align="end">
              <Dropdown.Toggle
                as="button"
                className="sensor-detail-icon-link sensor-detail-actions-toggle border-0 bg-transparent p-1"
                id="sensor-detail-actions-dropdown"
                title={t("more_actions")}
              >
                <MoreIcon size="24px" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as="div" className="px-2">
                  <ExportSensor serials={[sensor.id]} name={sensor.name} sensors={[sensor]} />
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        <div style={{ background: "white", padding: "0 12px" }}>
          <div className="notes mb-4">
            <ul className="p-0 mb-0 list-unstyled">
              {/* <li className="pb-2">
                <span style={{ fontWeight: "500" }}>{t("sensor_name")}:</span> {sensor.name}
              </li> */}
              <li className="pb-2">
                <span style={{ fontWeight: "500" }}>
                  {sensor.network_type == "LW" ? t("hardware_serial") : t("imei")}:
                </span>{" "}
                {sensor.network_type == "LW" ? sensor.hardware_serial : sensor.imei}
              </li>
              {sensor.pulse_sensor !== null && (
                <>
                  <li className="pb-2">
                    <span style={{ fontWeight: "500" }}>{t("threshold")}</span>:{" "}
                    <i> {sensor?.threshold}</i>
                  </li>
                </>
              )}
              <li className="pb-2">
                <span style={{ fontWeight: "500" }}>{t("last_battery")}: </span>
                {sensor.battery ? sensor.battery + "%" : t("unknown")}
              </li>
              {/* <li className="pb-2">
                <span style={{ fontWeight: "500" }}>{t("health_state")}</span>:{" "}
                {
                  {
                    0: healthState && healthState[0].value,
                    1: healthState && healthState[1].value,
                    2: healthState && healthState[2].value,
                    3: healthState && healthState[3].value,
                    4: healthState && healthState[4].value,
                  }[sensor.health_state]
                }
              </li> */}
              <li className="pb-2">
                <span style={{ fontWeight: "500" }}>{t("sensor_state")}</span>:{" "}
                {
                  {
                    // TODO: if actual last sent is within the range, set it to working
                    0: t(sensorState[0]),
                    1: t(sensorState[1]),
                    2: t(sensorState[2]),
                    3: isSending ? t(sensorState[0]) : t(sensorState[3]),
                    4: t(sensorState[4]),
                  }[sensor.sensor_state]
                }
              </li>
              {/* <li className="pb-2">
                <span style={{ fontWeight: "500" }}>{t("last_send")}</span>:{" "}
                {moment(new Date(lastSend)).format("DD.MM.YYYY HH:mm")}
              </li>
              <li className="pb-2">
                <span style={{ fontWeight: "500" }}>{t("last_value")}</span>: {lastSendValue}
              </li> */}
              {/* <li>
                <br />
                <b>{t("graph")}:</b>
              </li> */}
            </ul>
            {/* <SensorPlot sensor={sensor} plotType={plotType} setPlotType={setPlotType} /> */}
            <br></br>
            <h4 className="mb-3" data-testid="test-last-10-measurements">
              {t("last_10_measurements")}
            </h4>
            {isLoadingLastSend ? (
              <Spin />
            ) : (
              data && (
                <DataList
                  data={data}
                  // lastSend={lastSend}
                  // lastSendValue={lastSendValue}
                  sensorType={sensorType}
                  value={value}
                />
              )
            )}
          </div>
        </div>
      </div>
      {openForm && (
        <SensorForm
          isUpdate={isUpdate}
          detail={sensor}
          setIsUpdate={setIsUpdate}
          show={openForm}
          setOpenForm={setOpenForm}
        />
      )}
    </>
  );
}
const dataList = [
  { time: "10:00", resistance: "5Ω", temperature: "22°C", battery: "90%" },
  { time: "10:05", resistance: "5.1Ω", temperature: "22.5°C", battery: "88%" },
  { time: "10:10", resistance: "5.2Ω", temperature: "23°C", battery: "85%" },
];

const DataListOld = () => {
  return (
    <div>
      <h2>Messwerte</h2>
      <ul>
        <li>
          <strong>Zeit</strong> - <strong>Widerstand</strong> - <strong>Temperatur</strong> -{" "}
          <strong>Batterie</strong>
        </li>
        {dataList.map((item, index) => (
          <li key={index}>
            {item.time} - {item.resistance} - {item.temperature} - {item.battery}
          </li>
        ))}
      </ul>
    </div>
  );
};

// <li className="pb-2">
//   <span style={{ fontWeight: "500" }}>{t("last_send")}</span>:{" "}
//   {moment(new Date(lastSend)).format("DD.MM.YYYY HH:mm")}
// </li>
// <li className="pb-2">
//   <span style={{ fontWeight: "500" }}>{t("last_value")}</span>: {lastSendValue}
// </li>
function DataTable({ lastSend, lastSendValue }) {
  // TODO: Depending on the sensor type, we need to define the headers of the table we want to show and then get them from the backend
  // Pulse: Widerstand, Temperatur, Batterie
  // FDR: Bodenfeuchte, Temperatur, Batterie
  // Watermark: SWT, Temperatur, Batterie
  const index = 0;
  const { t } = useTranslation();
  return (
    // <div style={{ maxWidth: "400px", margin: "20px auto", fontFamily: "Arial, sans-serif" }}>
    <div>
      <h2 style={{ fontWeight: "bold", textAlign: "center" }}>{t("last_measurements")}</h2>
      <table border="0" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ backgroundColor: "#ddd", borderBottom: "2px solid black" }}>
            <th style={{ textAlign: "left", padding: "10px" }}>{t("time")}</th>
            <th style={{ textAlign: "right", padding: "10px" }}>{t("resistance")}</th>
            {/* <th>Temperatur</th>
            <th>Batterie</th> */}
          </tr>
        </thead>
        <tbody>
          {/* {dataList.map((item, index) => ( */}
          {/* <tr key={index}> */}
          {/* <tr key={0}> */}
          <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff" }}>
            <td style={{ textAlign: "left", padding: "10px" }}>
              {moment(new Date(lastSend)).format("DD.MM.YYYY HH:mm")}
            </td>
            <td style={{ textAlign: "right", padding: "10px" }}>{lastSendValue}</td>
            {/* <td>{item.temperature}</td>
              <td>{item.battery}</td> */}
          </tr>
          {/* ))} */}
        </tbody>
      </table>
    </div>
  );
}

const Spin = () => {
  return (
    <div className="spin">
      <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
    </div>
  );
};

function DataList({ lastSend, lastSendValue, data, sensorType, value }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const timeString =
    lang == "de" ? "de-DE" : lang == "en" ? "en-UK" : lang == "it" ? "it-IT" : "de-DE";
  const tempHeader = t("temperature");
  const timeHeader = t("time");
  const valueHeader = value ? `${t(value)}` : t("resistance");
  const batHeader = t("battery");
  const columns = useMemo(() => {
    const cols = [
      // statusHeader(t),
      {
        Header: timeHeader,
        accessor: "_time",
        Cell: ({ value }) => {
          // Datum in ein schöneres Format umwandeln
          const formattedDate = new Date(value).toLocaleString(timeString, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          return formattedDate;
        },
      },

      {
        Header: valueHeader,
        accessor: value ? value : "resistance",
        Cell: ({ value }) => {
          if (value === undefined) {
            return;
          }
          // Temperatur runden
          const roundedValue = value.toFixed(1); // Eine Nachkommastelle
          const unit =
            sensorType == "pulse"
              ? "kΩ"
              : sensorType == "soil_moisture"
              ? "%"
              : sensorType == "soil_water_tension"
              ? "kPa"
              : sensorType == "dendrometer"
              ? "µm"
              : "";
          return `${roundedValue} ${unit}`;
        },
      },
      // sensorType != "dendrometer" && {
      {
        Header: tempHeader,
        // accessor: sensorType == "soil_moisture" ? "temp_soil" : "temperature",
        accessor: "temperature",
        Cell: ({ value }) => {
          if (value === undefined) {
            return;
          }
          // Temperatur runden
          const roundedTemperature = value.toFixed(1); // Eine Nachkommastelle
          return `${roundedTemperature} °C`;
        },
      },
      {
        Header: batHeader,
        accessor: "battery",
        Cell: ({ value }) => {
          // Temperatur runden
          const roundedValue = value.toFixed(3); // Eine Nachkommastelle
          const unit = "V";
          return `${roundedValue} ${unit}`;
        },
      },
    ];
    // return cols.filter(Boolean);
    // if (sensorType !== "dendrometer") {
    //   cols.splice(2, 0, {
    //     Header: tempHeader,
    //     accessor: "temperature",
    //     Cell: ({ value }) => {
    //       if (value === undefined) return null;
    //       return `${value.toFixed(1)} °C`;
    //     },
    //   });
    // }
    return cols;
  }, [sensorType, t, value]);
  return (
    <>
      {/* {" "} */}
      {/* {data && ( */}
      <ReactTable
        columns={columns}
        // data={[{ time: lastSend, value: lastSendValue }]}
        data={Array.isArray(data) ? data : []}
        // onRowClicked={onRowClicked}
        activeId={0}
        pagesize={10}
        size={[10, 20, 30, 40, 50]}
        pagination={false}
        filter={false}
      />
      {/* )} */}
    </>
  );
}
